import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import type { PasskeyFailure, PasskeyOutcome } from './types';
import { withoutEmptyHints } from './options';

export { withoutEmptyHints };

// 9.33.9.3 — the browser's own WebAuthn. The passkey stays with the browser or the
// device; only the signed answer reaches nosubject.

export function passkeysAvailable(): boolean {
  try {
    return browserSupportsWebAuthn();
  } catch {
    return false;
  }
}

/**
 * `NotAllowedError` means either "the person cancelled" or, in Safari, "this was
 * not started by a tap". The caller cannot tell them apart, so it may offer one
 * more tap (`needsGesture`) before treating it as a cancel.
 */
export function webFailure(error: unknown): PasskeyFailure {
  const name = (error as { name?: unknown } | null)?.name;
  const code = (error as { code?: unknown } | null)?.code;
  if (code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED' || name === 'InvalidStateError') return 'exists';
  if (name === 'NotAllowedError' || name === 'AbortError') return 'needsGesture';
  if (code === 'ERROR_INVALID_DOMAIN' || code === 'ERROR_INVALID_RP_ID' || name === 'SecurityError'
    || name === 'NotSupportedError') return 'unsupported';
  return 'failed';
}

export async function createPasskey(options: object): Promise<PasskeyOutcome> {
  try {
    const value = await startRegistration({ optionsJSON: withoutEmptyHints(options) as PublicKeyCredentialCreationOptionsJSON });
    return { ok: true, value };
  } catch (error) {
    return { ok: false, reason: webFailure(error) };
  }
}

export async function getPasskey(options: object): Promise<PasskeyOutcome> {
  try {
    const value = await startAuthentication({ optionsJSON: withoutEmptyHints(options) as PublicKeyCredentialRequestOptionsJSON });
    return { ok: true, value };
  } catch (error) {
    return { ok: false, reason: webFailure(error) };
  }
}
